export const createDecks = ({ helpers, asset }) => {
  const {
    ExternalLink,
    SoftCard,
    EmojiCard,
    CleanList,
    SectionLabel,
    ConceptMark,
    ScreenshotFrame,
    NumberedList,
    ComparisonTable,
    BigStatement,
    Timeline,
  } = helpers;

  return {
     overview: {
  label: "Overview",
  description: "core pitch",
  slides: [
    {
      title: "",
      density: "medium",
      content: (
        <div className="relative overflow-hidden px-4 py-4 text-center sm:px-8 sm:py-5">
          <div className="absolute right-4 top-4 sm:right-8 sm:top-6">
            <ExternalLink compact />
          </div>

          <div className="mx-auto flex min-h-[520px] max-w-6xl flex-col justify-center">
            <h1 className="font-mono text-4xl font-black leading-[0.94] tracking-tight text-gray-950 sm:text-5xl md:text-6xl xl:text-7xl">
              You finished
              <br />
              the language course.
              <br />
              Now what?
            </h1>

            <div className="mx-auto mt-6 inline-flex rounded-full border border-red-200 bg-red-50/75 px-6 py-2 text-[10px] font-black uppercase tracking-[0.24em] text-red-700 shadow-sm sm:text-[11px] sm:tracking-[0.28em]">
              The Post-Learning Plateau
            </div>

            <div className="mx-auto mt-4 h-1.5 w-48 rounded-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-500 sm:w-72" />

            <div className="mt-7 grid gap-4 text-left md:grid-cols-3 xl:gap-5">
              <SoftCard className="min-h-[132px] border-white/70 bg-white/75 shadow-sm">
                <div className="mb-4 text-2xl">😶</div>
                <div className="text-lg font-black leading-snug text-gray-900 xl:text-xl">
                  You can follow lessons.
                  <br />
                  Real speech still feels fragile.
                </div>
              </SoftCard>

              <SoftCard className="min-h-[132px] border-white/70 bg-white/75 shadow-sm">
                <div className="mb-4 text-2xl">🏫</div>
                <div className="text-lg font-black leading-snug text-gray-900 xl:text-xl">
                  Courses build knowledge.
                  <br />
                  Not conversation confidence.
                </div>
              </SoftCard>

              <SoftCard className="min-h-[132px] border-white/70 bg-white/75 shadow-sm">
                <div className="mb-4 text-2xl">🕳️</div>
                <div className="text-lg font-black leading-snug text-gray-900 xl:text-xl">
                  No clear bridge
                  <br />
                  to real interaction.
                </div>
              </SoftCard>
            </div>
          </div>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_35%),radial-gradient(circle_at_bottom_right,_#fce7f3,_transparent_35%),linear-gradient(135deg,#ffffff,#f8fafc)]",
    },

    {
      title: "Existing Options Help — But Each Leaves A Gap",
      density: "medium",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-4 py-2 text-center sm:px-5">
          <h3 className="mx-auto max-w-5xl text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Learners try several paths outside the classroom.
          </h3>

          <p className="mx-auto max-w-4xl text-base font-semibold leading-relaxed text-gray-600 sm:text-lg">
            These paths help learners fight the plateau — but each leaves part
            of speech practice unsolved.
          </p>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4 xl:gap-5">
            {[
              ["🌍", "Immersion", "Exposure", "Easy to repeat habits"],
              ["📱", "Language apps", "Vocabulary", "Limited real interaction"],
              ["🧑‍🏫", "Private tutors", "Correction", "Can feel tense"],
              ["☕", "Language cafés", "Community", "Little continuity"],
            ].map(([emoji, title, pro, con]) => (
              <SoftCard
                key={title}
                className="bg-white/82 text-left shadow-sm ring-1 ring-blue-100"
              >
                <div className="mb-4 text-3xl">{emoji}</div>

                <h4 className="mb-4 text-lg font-black text-gray-950 xl:text-xl">
                  {title}
                </h4>

                <div className="space-y-3">
                  <div>
                    <div className="mb-1 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-600">
                      Strength
                    </div>
                    <div className="text-base font-bold text-gray-900">
                      {pro}
                    </div>
                  </div>

                  <div>
                    <div className="mb-1 text-[10px] font-black uppercase tracking-[0.18em] text-red-600">
                      Gap
                    </div>
                    <div className="text-base font-bold text-gray-700">
                      {con}
                    </div>
                  </div>
                </div>
              </SoftCard>
            ))}
          </div>

          <BigStatement
            title="The missing piece is structured social speech practice."
            tone="purple"
            compact
          >
            Practice that combines repetition, feedback, and real human
            interaction.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#eff6ff)]",
    },

   

   {
  title: "🎤🕺 Let’s Stick Together",
  density: "medium",
  content: (
    <div className="mx-auto flex max-w-7xl flex-col justify-center px-4 py-4 text-center sm:px-6">
      <div className="mx-auto max-w-2xl">
        <div className="text-2xl italic leading-snug text-gray-700 sm:text-3xl">
          “A person needs another person.”
        </div>

        <div className="mt-2 text-xs font-black uppercase tracking-[0.22em] text-gray-400">
          Solaris · Tarkovsky
        </div>
      </div>

      <h3 className="mx-auto mt-8 max-w-5xl text-2xl font-extrabold leading-[1.18] text-gray-950 sm:text-3xl md:text-[2.55rem]">
        Can online practice combine classroom guidance,
        <br className="hidden md:block" />
        language-café ease, and individual pace?
      </h3>

      <div className="mx-auto mt-14 max-w-4xl rounded-[2.5rem] border border-purple-100 bg-white/78 px-7 py-9 text-center shadow-sm ring-1 ring-purple-100 sm:px-8">
        <div className="mb-5 flex justify-center gap-4 text-5xl">
          <span>🧍</span>
          <span>🤝</span>
          <span>👥</span>
          <span>🎭</span>
        </div>

        <div className="text-3xl font-black leading-tight text-gray-950 sm:text-4xl">
          We may have a good idea for this.
        </div>

        <div className="mx-auto mt-5 max-w-2xl text-base font-semibold leading-relaxed text-gray-600 sm:text-lg">
          A more social and continuous form of speech practice.
        </div>
      </div>
    </div>
  ),
  bgColor:
    "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_32%),radial-gradient(circle_at_bottom_right,_#f3e8ff,_transparent_32%),linear-gradient(135deg,#ffffff,#f8fafc)]",
},

{
  title: "The Catchphraser Idea",
  density: "medium",
  content: (
    <div className="mx-auto flex max-w-7xl flex-col justify-center px-4 py-4 text-center sm:px-6">
      <h3 className="mx-auto max-w-5xl text-3xl font-black leading-[1.08] text-gray-950 sm:text-5xl md:text-6xl">
        Add a social layer
        <br />
        before immersion.
      </h3>

      <p className="mx-auto mt-6 max-w-4xl text-base font-semibold leading-relaxed text-gray-600 sm:text-lg xl:text-xl">
        This is not immersion yet. It is the step before immersion: a friendly,
        semi-artificial environment where learners get used to speaking with
        people in the new language, become easier to understand, and build
        confidence for real conversations.
      </p>

      <div className="mx-auto mt-10 grid w-full max-w-6xl gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          ["🔁", "Prepare", "repeat useful speech"],
          ["🤖", "Notice", "get feedback first"],
          ["🤝", "Interact gently", "review and help peers"],
          ["🎭", "Rehearse", "practice before real life"],
        ].map(([emoji, title, text]) => (
          <SoftCard
            key={title}
            className="flex min-h-[165px] flex-col items-center justify-center bg-white/85 text-center shadow-sm ring-1 ring-purple-100"
          >
            <div className="mb-4 text-5xl">{emoji}</div>

            <div className="text-xl font-black leading-tight text-gray-950">
              {title}
            </div>

            <div className="mt-3 text-sm font-bold text-gray-500">
              {text}
            </div>
          </SoftCard>
        ))}
      </div>

      <div className="mx-auto mt-10 max-w-4xl rounded-[2rem] bg-white/85 px-7 py-5 text-lg font-black leading-relaxed text-gray-950 shadow-sm ring-1 ring-gray-100 sm:text-xl">
        Not full immersion —
        <br className="sm:hidden" />
        preparation for it.
      </div>
    </div>
  ),
  bgColor:
    "bg-[radial-gradient(circle_at_top_left,_#f3e8ff,_transparent_32%),radial-gradient(circle_at_bottom_right,_#dcfce7,_transparent_32%),linear-gradient(135deg,#ffffff,#f8fafc)]",
},
    {
      title: "The First Social Layer: Peer Review",
      density: "medium",
      content: (
        <div className="mx-auto flex max-w-7xl flex-col justify-center px-4 py-4 text-center sm:px-6">
          <h3 className="mx-auto max-w-5xl text-3xl font-black leading-tight text-gray-950 sm:text-5xl md:text-6xl">
            Learners gradually learn
            <br />
            how to hear and describe speech.
          </h3>

          <div className="mx-auto mt-6 max-w-4xl">
            <div className="text-lg italic leading-relaxed text-gray-600 sm:text-xl">
              “What a child can do in cooperation today, he will do alone
              tomorrow.”
            </div>

            <div className="mt-2 text-xs font-black uppercase tracking-[0.22em] text-gray-400">
              Vygotsky
            </div>
          </div>

          <div className="mx-auto mt-10 w-full max-w-5xl">
            <div className="rounded-[2rem] border border-emerald-100 bg-white/82 px-5 py-7 shadow-sm ring-1 ring-emerald-100 sm:px-8 sm:py-9">
              <div className="grid items-center gap-7 md:grid-cols-[1fr_140px_1fr]">
                <div className="flex flex-col items-center">
                  <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-blue-50 text-5xl shadow-sm ring-1 ring-blue-100 sm:h-24 sm:w-24 sm:text-6xl">
                    🧑‍🦱
                  </div>

                  <div className="text-2xl font-black leading-tight text-gray-950 sm:text-3xl">
                    Assess
                    <br />
                    others
                  </div>
                </div>

                <div className="flex flex-col items-center justify-center">
                  <div className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50 text-4xl shadow-sm ring-1 ring-emerald-100 sm:h-24 sm:w-24">
                    💬
                  </div>

                  <div className="mt-4 text-sm font-black uppercase tracking-[0.18em] text-emerald-700">
                    shared terms
                  </div>
                </div>

                <div className="flex flex-col items-center">
                  <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-purple-50 text-5xl shadow-sm ring-1 ring-purple-100 sm:h-24 sm:w-24 sm:text-6xl">
                    👩‍🦰
                  </div>

                  <div className="text-2xl font-black leading-tight text-gray-950 sm:text-3xl">
                    Hear your
                    <br />
                    own mistakes
                  </div>
                </div>
              </div>

              <div className="mx-auto mt-8 max-w-3xl rounded-3xl bg-emerald-50/70 px-6 py-5 text-base font-black leading-relaxed text-gray-950 sm:text-lg">
                Learners develop assessment skill together with practical
                pronunciation terminology.
              </div>
            </div>
          </div>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dcfce7,_transparent_32%),radial-gradient(circle_at_bottom_right,_#ede9fe,_transparent_32%),linear-gradient(135deg,#ffffff,#f8fafc)]",
    },

    {
      title: "Every Correction Can Help The Next Learner",
      density: "screenshot",
      content: (
        <div className="mx-auto flex max-w-7xl flex-col justify-center px-4 py-2 text-center">
          <h3 className="mx-auto max-w-5xl text-3xl font-extrabold leading-[1.08] text-gray-950 md:text-[3.2rem]">
            Feedback improves learners.
            <br />
            Good feedback improves the system.
          </h3>

          <div className="mt-8 grid items-center gap-6 xl:grid-cols-[1.8fr_0.55fr]">
            <div className="group relative overflow-visible">
              <a
                href={asset("rated.png")}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <div className="overflow-hidden rounded-[2rem] border border-gray-200/80 bg-white p-2 shadow-[0_24px_80px_rgba(15,23,42,0.12)] transition-all duration-300 group-hover:scale-[1.01] group-hover:shadow-[0_38px_110px_rgba(15,23,42,0.17)] sm:p-3">
                  <div className="h-[300px] overflow-hidden rounded-[1.45rem] border border-gray-100 bg-white sm:h-[430px] xl:h-[475px]">
                    <img
                      src={asset("rated.png")}
                      alt="Community pronunciation review with comments and marked pronunciation issues"
                      className="h-full w-full object-cover object-[center_63%]"
                    />
                  </div>
                </div>

                <div className="pointer-events-none absolute right-5 top-5 hidden opacity-0 transition-opacity duration-300 group-hover:opacity-100 lg:block">
                  <div className="rounded-full bg-white/95 px-4 py-2 text-sm font-black text-gray-700 shadow-lg ring-1 ring-gray-200 backdrop-blur-sm">
                    🔍 Click to enlarge
                  </div>
                </div>
              </a>
            </div>

            <SoftCard className="flex min-h-[auto] flex-col justify-center bg-gradient-to-br from-emerald-50/95 to-white text-left shadow-sm ring-1 ring-emerald-100 xl:min-h-[475px]">
              <div className="grid gap-5 sm:grid-cols-3 xl:block xl:space-y-8">
                <div>
                  <div className="text-lg font-black leading-snug text-gray-900 xl:text-xl">
                    L1→L2 pronunciation patterns
                  </div>

                  <div className="mt-2 text-sm font-semibold leading-relaxed text-gray-500">
                    Similar learner difficulties become easier to identify.
                  </div>
                </div>

                <div className="hidden h-px bg-emerald-100 xl:block" />

                <div>
                  <div className="text-lg font-black leading-snug text-gray-900 xl:text-xl">
                    Reusable correction strategies
                  </div>

                  <div className="mt-2 text-sm font-semibold leading-relaxed text-gray-500">
                    Useful explanations can support future learners.
                  </div>
                </div>

                <div className="hidden h-px bg-emerald-100 xl:block" />

                <div>
                  <div className="text-lg font-black leading-snug text-gray-900 xl:text-xl">
                    Better AI assessment
                  </div>

                  <div className="mt-2 text-sm font-semibold leading-relaxed text-gray-500">
                    Community interaction helps improve feedback quality over
                    time.
                  </div>
                </div>
              </div>
            </SoftCard>
          </div>

          <div className="mx-auto mt-6 max-w-4xl rounded-3xl bg-white/80 px-6 py-4 text-base font-black leading-relaxed text-gray-950 shadow-sm ring-1 ring-gray-100 sm:px-8 sm:text-lg">
            Every useful correction can help the next learner.
          </div>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dcfce7,_transparent_35%),linear-gradient(135deg,#ffffff,#f8fafc)]",
    },

    {
      title: "Review Becomes Dialogue",
      density: "medium",
      content: (
        <div className="mx-auto flex max-w-7xl flex-col justify-center px-4 py-4 text-center sm:px-5">
          <div className="mx-auto max-w-4xl">
            <div className="text-2xl italic leading-relaxed text-gray-600 md:text-3xl">
              “Through others, we become ourselves.”
            </div>

            <div className="mt-3 text-xs font-black uppercase tracking-[0.24em] text-gray-400">
              Vygotsky
            </div>
          </div>

          <h3 className="mx-auto mt-8 max-w-5xl text-3xl font-black leading-[1.08] text-gray-950 sm:text-5xl md:text-[3.6rem]">
            Peer review naturally evolves
            <br className="hidden md:block" />
            into shared speech activity.
          </h3>

          <div className="mx-auto mt-10 grid w-full max-w-6xl gap-5 lg:grid-cols-[1fr_auto_1fr]">
            <SoftCard className="bg-gradient-to-br from-blue-50 to-white text-center ring-1 ring-blue-100">
              <div className="mb-5 flex items-center justify-center gap-4 text-5xl sm:text-6xl">
                <span>🧍</span>
                <span>🧍‍♀️</span>
              </div>

              <h4 className="text-2xl font-black leading-tight text-gray-950">
                Collaborative
                <br />
                preparation
              </h4>
            </SoftCard>

            <div className="flex items-center justify-center">
              <div className="rounded-[2rem] bg-gradient-to-r from-purple-100 to-pink-100 px-7 py-6 shadow-sm ring-1 ring-purple-200">
                <div className="mb-3 flex justify-center text-5xl">
                  👥💻
                </div>

                <div className="text-xl font-black leading-tight text-gray-950">
                  Dialogue
                  <br />
                  challenge engine
                </div>
              </div>
            </div>

            <SoftCard className="bg-gradient-to-br from-pink-50 to-white text-center ring-1 ring-pink-100">
              <div className="mb-5 flex justify-center text-5xl sm:text-6xl">
                🎭
              </div>

              <h4 className="text-2xl font-black leading-tight text-gray-950">
                Shared
                <br />
                dialogue
              </h4>
            </SoftCard>
          </div>

          <div className="mx-auto mt-10 max-w-4xl rounded-3xl bg-white/80 px-7 py-5 text-lg font-black leading-relaxed text-gray-950 shadow-sm ring-1 ring-gray-100 sm:text-xl">
            Review becomes challenge. Challenge becomes dialogue. Dialogue
            becomes rehearsal.
          </div>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_30%),radial-gradient(circle_at_bottom_right,_#fce7f3,_transparent_32%),linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Rehearsal Gives Practice A Reason",
      density: "medium",
      content: (
        <div className="mx-auto flex max-w-7xl flex-col justify-center px-4 py-4 text-center sm:px-6">
          <div className="mx-auto max-w-5xl">
            <div className="text-xl italic leading-relaxed text-gray-600 sm:text-2xl md:text-3xl">
              “Learning awakens a variety of internal developmental processes
              that operate only when interacting with others.”
            </div>

            <div className="mt-4 text-xs font-black uppercase tracking-[0.24em] text-gray-400">
              Vygotsky
            </div>
          </div>

          <div className="mx-auto mt-12 grid w-full max-w-6xl items-stretch gap-5 md:grid-cols-[1fr_56px_1fr] md:gap-8">
            <SoftCard className="bg-gradient-to-br from-amber-50 to-white text-left shadow-sm ring-1 ring-amber-100">
              <div className="mb-7 flex items-center gap-4">
                <div className="text-5xl">🔁</div>

                <div>
                  <h4 className="text-2xl font-black text-gray-950">
                    Solo loop
                  </h4>

                  <div className="text-xs font-black uppercase tracking-[0.18em] text-amber-700">
                    isolated app practice
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-lg font-black leading-snug text-gray-800 sm:text-xl">
                <div>repeat alone</div>
                <div>track streaks</div>
                <div>motivation fades</div>
              </div>
            </SoftCard>

            <div className="hidden items-center justify-center md:flex">
              <div className="text-5xl font-black text-purple-300">→</div>
            </div>

            <SoftCard className="bg-gradient-to-br from-purple-50 to-white text-left shadow-sm ring-1 ring-purple-100">
              <div className="mb-7 flex items-center gap-4">
                <div className="text-5xl">🎭</div>

                <div>
                  <h4 className="text-2xl font-black text-gray-950">
                    Shared rehearsal
                  </h4>

                  <div className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">
                    toward a gathering
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-lg font-black leading-snug text-gray-800 sm:text-xl">
                <div>prepare together</div>
                <div>toward a real session</div>
                <div>practice starts moving</div>
              </div>
            </SoftCard>
          </div>

          <div className="mx-auto mt-10 max-w-3xl rounded-[1.8rem] bg-white/70 px-7 py-4 text-lg font-black leading-relaxed text-gray-900 shadow-sm ring-1 ring-gray-100">
            Rehearsal turns repetition into preparation for something shared.
          </div>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#fef3c7,_transparent_28%),radial-gradient(circle_at_bottom_right,_#f3e8ff,_transparent_30%),linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "From App Practice To Guided Speech Events",
      density: "medium",
      content: (
        <div className="mx-auto flex max-w-7xl flex-col justify-center px-4 py-4 text-center sm:px-6">
          <h3 className="mx-auto max-w-5xl text-3xl font-black leading-tight text-gray-950 sm:text-5xl md:text-6xl">
            Speech practice can evolve
            <br />
            into guided rehearsal.
          </h3>

          <p className="mx-auto mt-6 max-w-4xl text-base leading-relaxed text-gray-600 sm:text-lg xl:text-xl">
            Teachers, actors, tutors, hosts, and facilitators can organize
            shared practice sessions around speech, dialogue, and performance.
          </p>

          <div className="mx-auto mt-10 grid w-full max-w-5xl items-stretch gap-5 md:grid-cols-[1fr_90px_1fr] md:gap-7">
            <SoftCard className="bg-gradient-to-br from-purple-50 to-white text-left shadow-md ring-1 ring-purple-100">
              <div className="mb-7 flex items-center gap-4">
                <div className="text-5xl">🧑‍🏫</div>

                <div>
                  <h4 className="text-2xl font-black text-gray-950 sm:text-3xl">
                    Facilitators
                  </h4>

                  <div className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">
                    human guidance layer
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-lg font-black leading-snug text-gray-800 sm:text-xl">
                <div>guide rehearsal</div>
                <div>correct in real time</div>
                <div>create commitment</div>
              </div>
            </SoftCard>

            <div className="hidden flex-col items-center justify-center md:flex">
              <div className="text-7xl font-black text-purple-300">→</div>

              <div className="mt-3 text-[10px] font-black uppercase tracking-[0.24em] text-purple-400">
                shared preparation
              </div>
            </div>

            <SoftCard className="bg-gradient-to-br from-emerald-50 to-white text-left shadow-sm ring-1 ring-emerald-100">
              <div className="mb-7 flex items-center gap-4">
                <div className="text-5xl">🎭</div>

                <div>
                  <h4 className="text-2xl font-black text-gray-950 sm:text-3xl">
                    Shared sessions
                  </h4>

                  <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">
                    rehearsal-oriented practice
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-lg font-black leading-snug text-gray-800 sm:text-xl">
                <div>dialogue rehearsal</div>
                <div>staged readings</div>
                <div>speech clubs</div>
              </div>
            </SoftCard>
          </div>

          <div className="mx-auto mt-12 max-w-4xl rounded-[2.2rem] bg-white/85 px-8 py-6 text-xl font-black leading-relaxed text-gray-950 shadow-sm ring-1 ring-gray-100 sm:text-2xl">
  Rehearsal gives speech practice
  <br className="hidden sm:block" />
  a social reason to continue.
</div>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dcfce7,_transparent_30%),radial-gradient(circle_at_bottom_right,_#f3e8ff,_transparent_32%),linear-gradient(135deg,#ffffff,#f8fafc)]",
    },

   
  ],
},
 pedagogy: {
  label: "Pedagogy",
  description: "learning theory",
  slides: [
    {
      title: "More Than Immersion",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-blue-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">
              Second-language acquisition
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              Exposure is important, but progress often depends on interaction,
              feedback, and opportunities to modify one’s output.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Immersion alone is not always enough.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            Intermediate learners often know enough to communicate, but not
            enough to speak with confidence, nuance, rhythm, humour, or social
            precision. They need structured situations where speech can be tried,
            adjusted, repeated, and made meaningful.
          </p>

          <div className="grid gap-4 md:grid-cols-4 xl:gap-5">
            {[
              ["Input", "varied language exposure"],
              ["Interaction", "negotiation of meaning"],
              ["Output", "attempts that can be modified"],
              ["Feedback", "noticing and repair"],
            ].map(([title, text]) => (
              <SoftCard
                key={title}
                className="bg-white/82 text-center shadow-sm ring-1 ring-blue-100"
              >
                <div className="text-lg font-black text-gray-950">
                  {title}
                </div>
                <div className="mt-3 text-sm font-semibold leading-relaxed text-gray-600">
                  {text}
                </div>
              </SoftCard>
            ))}
          </div>

          <BigStatement title="The goal is not exposure alone." compact>
            The goal is meaningful speech that can be attempted, noticed,
            repaired, and used again.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#eff6ff)]",
    },

    {
      title: "Intermediate Learners Need Expressive Range",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-purple-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">
              Communicative competence
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              Language mastery is not only grammatical or semantic. It is also
              social, pragmatic, prosodic, and affective.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            After the course, learners need more than correctness.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            They need practice with tone, timing, emotional colour, register,
            irony, hesitation, turn-taking, and social response — the parts of
            speech that make communication feel human.
          </p>

          <div className="grid gap-4 md:grid-cols-3 xl:gap-5">
            {[
              ["Prosody", "stress, rhythm, intonation, timing"],
              ["Pragmatics", "register, politeness, turn-taking"],
              ["Affect", "emotion, humour, hesitation, stance"],
            ].map(([title, text]) => (
              <SoftCard
                key={title}
                className="bg-white/82 text-center shadow-sm ring-1 ring-purple-100"
              >
                <div className="text-xl font-black text-gray-950">
                  {title}
                </div>
                <div className="mt-3 text-sm font-semibold leading-relaxed text-gray-600">
                  {text}
                </div>
              </SoftCard>
            ))}
          </div>

          <BigStatement title="The plateau is not only linguistic." tone="purple" compact>
            It is also social, expressive, and performative.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Why Rehearsal Matters",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-amber-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-amber-700">
              Drama-based language learning
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              Drama creates structured, repeatable, emotionally varied, socially
              meaningful speaking situations.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Theater is useful because it creates roles.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            A role gives the learner a reason to speak differently: with another
            intention, emotion, relation, rhythm, or level of formality. This
            helps learners move beyond their fixed personal routines.
          </p>

          <ComparisonTable
            leftTitle="Ordinary conversation"
            rightTitle="Rehearsed role"
            rows={[
              ["repeated personal topics", "varied communicative situations"],
              ["same speech habits", "new tone, rhythm, and register"],
              ["low preparation", "practice between meetings"],
              ["unclear progression", "shared goal and culmination"],
            ]}
          />

          <BigStatement title="Rehearsal makes repetition purposeful." tone="amber" compact>
            Learners are not just repeating; they are preparing for a shared
            communicative event.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#fffbeb)]",
    },

    {
      title: "Scripts Reduce Cognitive Load",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-purple-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">
              Scaffolding
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              When the language material is structured, learners can focus more
              deeply on delivery, timing, pronunciation, and response.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Given words create space for better speech.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            Scripts reduce the pressure to invent content. This allows learners
            to concentrate on how the utterance is delivered: rhythm, stress,
            intonation, emotional force, and interactional timing.
          </p>

          <ComparisonTable
            leftTitle="Free conversation"
            rightTitle="Script-based dialogue"
            rows={[
              ["what should I say?", "how should I say it?"],
              ["topic search", "delivery focus"],
              ["grammar pressure", "voice and timing"],
              ["conversation anxiety", "prepared interaction"],
            ]}
          />

          <BigStatement title="The script is not the goal." tone="purple" compact>
            It is a scaffold for socially usable speech.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Why Group Practice Matters",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-emerald-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">
              Peer interaction
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              Learning can happen not only through teacher correction, but also
              through participation, comparison, imitation, and peer feedback.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            A group changes the social dynamics of correction.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            One-to-one tutoring is valuable, but it can become asymmetrical:
            one person evaluates, the other is evaluated. In a group, learners
            see that others struggle too, notice similar mistakes, and learn by
            observing repair in others before recognizing it in themselves.
          </p>

          <div className="grid gap-4 md:grid-cols-3 xl:gap-5">
            {[
              ["Shared difficulty", "learners see that struggle is normal"],
              ["Peer noticing", "mistakes in others become easier to identify"],
              ["Collective repair", "feedback becomes part of group practice"],
            ].map(([title, text]) => (
              <SoftCard
                key={title}
                className="bg-white/82 text-center shadow-sm ring-1 ring-emerald-100"
              >
                <div className="text-xl font-black text-gray-950">
                  {title}
                </div>
                <div className="mt-3 text-sm font-semibold leading-relaxed text-gray-600">
                  {text}
                </div>
              </SoftCard>
            ))}
          </div>

          <BigStatement title="Correction becomes less confrontational." tone="green" compact>
            Learners are not only corrected by an authority; they move through
            the same process together.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#ecfdf5)]",
    },

    {
      title: "From Language Café To Rehearsal Community",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-blue-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">
              Social learning design
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              Language cafés provide access and community. Rehearsal adds
              preparation, continuity, progression, and shared purpose.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            The missing ingredient is continuity.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            Drop-in conversation is valuable, but learners may return to the
            same topics and habits. Rehearsal creates commitment: participants
            prepare between meetings and move toward something shared.
          </p>

          <ComparisonTable
            leftTitle="Language café"
            rightTitle="Rehearsal community"
            rows={[
              ["low-pressure access", "structured preparation"],
              ["open conversation", "roles and tasks"],
              ["limited continuity", "progress across sessions"],
              ["community contact", "shared communicative goal"],
            ]}
          />

          <BigStatement title="Community becomes more powerful with structure." compact>
            The platform can support the work before, during, and after social
            practice.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#eff6ff)]",
    },

    {
      title: "The Platform Hypothesis",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-gray-200 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-gray-600">
              Research-informed, not yet product-proven
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              The exact platform still needs validation, but its components come
              from well-established traditions in second-language learning.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Catchphraser combines several supported mechanisms.
          </h3>

          <div className="mx-auto grid max-w-6xl gap-4 text-left md:grid-cols-2 xl:gap-5">
            {[
              ["Interaction", "language is negotiated and modified"],
              ["Feedback", "learners notice and repair problems"],
              ["Task repetition", "practice becomes more fluent over time"],
              ["Role-play", "speech gains emotional and social variation"],
              ["Peer learning", "learners improve by reviewing others"],
              ["Shared goals", "motivation comes from participation, not only streaks"],
            ].map(([title, text]) => (
              <SoftCard
                key={title}
                className="bg-white/82 shadow-sm ring-1 ring-gray-100"
              >
                <div className="text-lg font-black text-gray-950">
                  {title}
                </div>
                <div className="mt-2 text-sm font-semibold leading-relaxed text-gray-600">
                  {text}
                </div>
              </SoftCard>
            ))}
          </div>

          <BigStatement title="The claim is modest but strong." tone="gray" compact>
            Not that the whole platform is already proven — but that it combines
            pedagogically meaningful mechanisms in one social practice system.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#f8fafc)]",
    },
  ],
},

    technology: {
      label: "Technology",
      description: "product workflows",
      slides: [
        {
          title: "AI-Supported Pronunciation Training",
          density: "screenshot",
          content: (
            <div className="mx-auto max-w-7xl px-3 py-0 sm:px-4">
              <div className="grid items-center gap-5 xl:grid-cols-[1.42fr_0.58fr] xl:gap-7">
                <div className="space-y-4 text-center xl:space-y-5">
                  <h3 className="mx-auto max-w-4xl text-3xl font-black leading-tight text-gray-950 xl:text-4xl">
                    AI feedback gives learners a private preparation loop.
                  </h3>

                  <div className="mx-auto max-w-4xl rounded-[1.75rem] border border-gray-200/80 bg-white p-3 shadow-[0_22px_70px_rgba(15,23,42,0.11)]">
                    <img
                      src={asset("kattenspiserikkefisk.png")}
                      alt="AI pronunciation feedback interface"
                      className="w-full rounded-[1.2rem] border border-gray-100 object-contain"
                    />

                    <div className="px-2 pt-2 text-center text-xs font-medium text-gray-500">
                      AI-supported pronunciation practice with phonetic feedback
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-1">
                  <SoftCard className="bg-gradient-to-br from-blue-50 to-white text-left shadow-none ring-1 ring-blue-100">
                    <h4 className="mb-3 text-xl font-black text-gray-950">
                      Private preparation
                    </h4>

                    <CleanList
                      accent="blue"
                      items={[
                        "safe repetition",
                        "immediate feedback",
                        "pronunciation correction",
                        "unlimited attempts",
                      ]}
                    />
                  </SoftCard>

                  <SoftCard className="bg-gradient-to-br from-purple-50 to-white text-left shadow-none ring-1 ring-purple-100">
                    <h4 className="mb-3 text-xl font-black text-gray-950">
                      Human activation
                    </h4>

                    <CleanList
                      accent="purple"
                      items={[
                        "real listener response",
                        "meaningful explanation",
                        "social interpretation",
                        "collaborative attention",
                      ]}
                    />
                  </SoftCard>
                </div>
              </div>
            </div>
          ),
          bgColor: "bg-[linear-gradient(135deg,#ffffff,#eff6ff)]",
        },

        {
          title: "Community Pronunciation Review",
          density: "screenshot",
          content: (
            <div className="mx-auto max-w-7xl space-y-4 px-3 py-0 sm:px-4 xl:space-y-5">
              <div className="text-center">
                <SectionLabel>Product Workflow</SectionLabel>
                <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
                  Learners can ask the community to review pronunciation.
                </h3>
              </div>

              <div className="grid items-center gap-5 lg:grid-cols-[1.35fr_0.65fr] xl:gap-7">
                <ScreenshotFrame
                  src={asset("rateme.png")}
                  alt="Rate Me pronunciation review interface"
                  caption="Learner request for structured pronunciation feedback"
                  imgClassName="max-h-[430px] object-contain bg-gray-50 xl:max-h-[500px]"
                />

                <SoftCard className="bg-blue-50/70 ring-1 ring-blue-100">
                  <h4 className="mb-4 text-xl font-black text-gray-950 xl:text-2xl">
                    Reviewers can
                  </h4>
                  <CleanList
                    accent="blue"
                    items={[
                      "replay the learner attempt",
                      "compare it with narrator audio",
                      "inspect rhythm and melody",
                      "mark unclear words or sounds",
                      "leave concrete suggestions",
                    ]}
                  />
                </SoftCard>
              </div>
            </div>
          ),
          bgColor:
            "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_35%),linear-gradient(135deg,#ffffff,#f8fafc)]",
        },

        {
          title: "Collaborative Repair",
          density: "screenshot",
          content: (
            <div className="mx-auto max-w-6xl space-y-4 px-3 py-0 text-center sm:px-4">
              <div className="mx-auto max-w-4xl rounded-[1.75rem] border border-gray-200/80 bg-white p-3 shadow-[0_22px_70px_rgba(15,23,42,0.10)]">
                <img
                  src={asset("rated.png")}
                  alt="Collaborative pronunciation review"
                  className="max-h-[300px] w-full rounded-[1.2rem] border border-gray-100 object-contain sm:max-h-[340px] xl:max-h-[365px]"
                />

                <div className="px-2 pt-2 text-center text-xs font-medium text-gray-500">
                  Pronunciation requests with ratings, marked segments, and discussion
                </div>
              </div>

              <div className="mx-auto max-w-4xl rounded-3xl border border-white/70 bg-gradient-to-r from-blue-50 via-white to-purple-50 px-5 py-4 shadow-[0_18px_50px_rgba(15,23,42,0.08)] sm:px-6">
                <p className="text-base font-semibold leading-relaxed text-gray-700 sm:text-lg">
                  Learners improve not only by speaking, but by listening,
                  evaluating, explaining, and repairing together.
                </p>
              </div>
            </div>
          ),
          bgColor: "bg-[linear-gradient(135deg,#f0fdf4,#faf5ff)]",
        },

        {
          title: "L1-Sensitive Feedback",
          density: "medium",
          content: (
            <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
              <h3 className="mx-auto max-w-5xl text-3xl font-black leading-tight text-gray-950 md:text-5xl">
                The same mistake often has different causes.
              </h3>

              <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
                Feedback becomes more useful when it knows what language background
                the learner is coming from.
              </p>

              <ComparisonTable
                leftTitle="Observed patterns"
                rightTitle="Adaptive guidance"
                leftColor="green"
                rows={[
                  ["learners mark difficult words", "explanations fit the learner’s L1"],
                  ["reviewers notice recurring errors", "practice targets the likely cause"],
                  ["similar learners struggle in similar places", "examples become more precise"],
                  ["corrections accumulate over time", "feedback becomes reusable"],
                ]}
              />

              <BigStatement
                title="This creates accumulated pronunciation knowledge."
                tone="green"
                compact
              >
                A Russian speaker and a French speaker learning Norwegian may need
                different explanations, examples, and repair paths.
              </BigStatement>
            </div>
          ),
          bgColor: "bg-[linear-gradient(135deg,#ffffff,#ecfdf5)]",
        },

        {
          title: "Prosody Visualization",
          density: "screenshot",
          content: (
            <div className="mx-auto max-w-7xl space-y-4 px-3 py-0 text-center sm:px-4">
              <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
                Seeing rhythm, stress, and intonation.
              </h3>

              <ScreenshotFrame
                src={asset("muchwarmerthere.png")}
                alt="Pitch contour visualization showing intonation groups and pitch variation across phrase boundaries"
                className="mx-auto max-w-6xl"
                imgClassName="max-h-[270px] w-full object-contain bg-gray-50 sm:max-h-[310px] xl:max-h-[335px]"
              />

              <div className="grid gap-4 md:grid-cols-3">
                <SoftCard className="bg-white/80 text-left shadow-none ring-1 ring-blue-100">
                  <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-blue-600">
                    Intonation groups
                  </div>

                  <p className="text-sm leading-relaxed text-gray-700">
                    Pitch develops across whole phrases, not only isolated words.
                  </p>
                </SoftCard>

                <SoftCard className="bg-white/80 text-left shadow-none ring-1 ring-purple-100">
                  <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-purple-600">
                    Word boundaries
                  </div>

                  <p className="text-sm leading-relaxed text-gray-700">
                    The contour travels through connected speech and reveals phrasing.
                  </p>
                </SoftCard>

                <SoftCard className="bg-white/80 text-left shadow-none ring-1 ring-pink-100">
                  <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-pink-600">
                    Stress perception
                  </div>

                  <p className="text-sm leading-relaxed text-gray-700">
                    Timing, prominence, and emphasis become easier to notice.
                  </p>
                </SoftCard>
              </div>
            </div>
          ),
          bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
        },
      ],
    },

 community: {
  label: "Community",
  description: "social speech practice",
  slides: [
    {
      title: "Speaking Develops Socially",
      density: "dense",
      content: (
        <div className="mx-auto max-w-6xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">

          <div className="mx-auto max-w-4xl rounded-3xl border border-purple-200 bg-gradient-to-r from-purple-100 via-white to-pink-100 px-5 py-4 shadow-[0_18px_50px_rgba(15,23,42,0.08)]">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">
              Lev Vygotsky
            </div>

            <div className="mt-2 text-lg italic leading-relaxed text-gray-800 sm:text-xl">
              “Human learning presupposes a specific social nature.”
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Speaking is not only language production.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            People learn to speak through interaction:
            listening, responding, repairing misunderstandings,
            adjusting timing, and gradually becoming comfortable
            participating with others.
          </p>

          <ComparisonTable
            leftTitle="Private AI practice"
            rightTitle="Social speech rehearsal"
            leftColor="blue"
            rightColor="purple"
            rows={[
              ["individual repetition", "interaction with listeners"],
              ["private correction", "shared repair and feedback"],
              ["simulated conversation", "social coordination"],
              ["isolated learning loop", "visible group progression"],
            ]}
          />

          <BigStatement
            title="AI can simulate conversation, but speech is still fundamentally social."
            tone="purple"
            compact
          >
            Catchphraser uses scripts, peer review, rehearsal,
            and guided interaction to help learners move from
            isolated practice toward real participation with other people.
          </BigStatement>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_32%),radial-gradient(circle_at_bottom_right,_#f3e8ff,_transparent_30%),linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Peers Make Progress Visible",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">

          <div className="mx-auto max-w-4xl rounded-3xl border border-purple-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">
              Lev Vygotsky
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              “What a learner can do in cooperation today,
              they can do independently tomorrow.”
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Learners improve by hearing others improve.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            Native-speaker models show the target,
            but peers show the process:
            hesitation, repair, adaptation, and gradual fluency.
          </p>

          <ComparisonTable
            leftTitle="Models"
            rightTitle="Peers"
            rows={[
              ["show the target", "show the path"],
              ["ideal pronunciation", "visible gradual improvement"],
              ["reference", "participation"],
              ["polished delivery", "realistic variability"],
            ]}
          />

          <BigStatement title="Speech confidence grows socially." tone="purple" compact>
            Learners become less inhibited when they hear people like themselves
            struggling, repairing, and gradually improving together.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Shared Practice Reduces Pressure",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">

          <div className="mx-auto max-w-4xl rounded-3xl border border-emerald-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">
              Mikhail Bakhtin
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              “To be means to communicate.”
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Learners speak more when they feel safe enough to continue.
          </h3>

          <ComparisonTable
            leftTitle="High-pressure speech"
            rightTitle="Shared rehearsal"
            leftColor="amber"
            rightColor="green"
            rows={[
              ["one learner is evaluated", "everyone is visibly learning"],
              ["mistakes feel personal", "mistakes become normal"],
              ["perfect models create pressure", "similar learners reduce shame"],
              ["correction interrupts confidence", "repair becomes collaborative"],
            ]}
          />

          <BigStatement title="The goal is usable participation." tone="green" compact>
            Learners gradually become more comfortable speaking because
            speech becomes shared work rather than isolated performance.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#ecfdf5)]",
    },

    {
      title: "Dialogue Rehearsal",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">

        <div className="mx-auto max-w-4xl rounded-3xl border border-blue-100 bg-white/85 px-5 py-4 shadow-sm">
  <div className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">
    Mikhail Bakhtin
  </div>

  <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
    “The utterance is filled with dialogic overtones.”
  </div>
</div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Prepared dialogue turns speaking into coordination.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            Instead of inventing every sentence from scratch,
            learners can focus on timing, pronunciation,
            response, listening, and interaction.
          </p>

          <Timeline
            steps={[
              ["🎧", "Prepare", "individual rehearsal", "listen, repeat, inspect prosody", "ring-blue-100"],
              ["💬", "Review", "peer correction", "mark unclear rhythm and sounds", "ring-purple-100"],
              ["👥", "Perform", "dialogue together", "response timing and coordination", "ring-pink-100"],
            ]}
          />

          <BigStatement title="The script is a scaffold for social speech." tone="purple" compact>
            Learners move gradually from guided rehearsal toward more natural interaction.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Guided Social Events",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">

          <div className="mx-auto max-w-4xl rounded-3xl border border-pink-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-pink-700">
              Lev Vygotsky
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              “Through others we become ourselves.”
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Guided events create stronger social motivation.
          </h3>

          <div className="grid gap-5 md:grid-cols-3 xl:gap-6">
            <EmojiCard
              emoji="👥"
              title="Learners"
              text="prepare, review, rehearse"
              className="ring-1 ring-blue-100"
            />

            <EmojiCard
              emoji="🧑‍🏫"
              title="Guides"
              text="structure sessions and progression"
              className="ring-1 ring-purple-100"
            />

            <EmojiCard
              emoji="🎙️"
              title="Events"
              text="radio plays, workshops, staged readings"
              className="ring-1 ring-pink-100"
            />
          </div>

          <BigStatement title="Speech practice becomes a coordinated social activity." tone="purple" compact>
            The long-term direction is not only AI tutoring,
            but communities practicing speech together.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
    },
  ],
},


gamification: {
  label: "Gamification",
  description: "rehearsal & dialogue challenges",
  slides: [
    {
      title: "Speech As Rehearsal",
      density: "dense",
      content: (
        <div className="mx-auto max-w-6xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-purple-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">
              Central analogy
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              An actor learns a role by rehearsing lines together with others.
              Speech practice can work in a similar way.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Learners rehearse dialogue roles.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            The system plays the opposite role while the learner practices responses,
            timing, pronunciation, and delivery inside a structured interaction.
          </p>

          <div className="grid gap-4 md:grid-cols-3 xl:gap-5">
            <SoftCard className="text-left ring-1 ring-blue-100">
              <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-blue-700">
                Round 1
              </div>

              <h4 className="mb-3 text-xl font-black text-gray-950">
                Learn the lines
              </h4>

              <CleanList
                accent="blue"
                items={[
                  "hear all dialogue lines",
                  "repeat own and opponent lines",
                  "practice pronunciation and rhythm",
                  "become familiar with the scene",
                ]}
              />
            </SoftCard>

            <SoftCard className="text-left ring-1 ring-purple-100">
              <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-purple-700">
                Round 2
              </div>

              <h4 className="mb-3 text-xl font-black text-gray-950">
                Rehearse the scene
              </h4>

              <CleanList
                accent="purple"
                items={[
                  "only opponent lines are played",
                  "produce missing responses",
                  "practice timing and recall",
                  "stay inside interaction flow",
                ]}
              />
            </SoftCard>

            <SoftCard className="text-left ring-1 ring-pink-100">
              <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-pink-700">
                Round 3
              </div>

              <h4 className="mb-3 text-xl font-black text-gray-950">
                Perform the role
              </h4>

              <CleanList
                accent="pink"
                items={[
                  "minimal or no support",
                  "score depends on hint usage",
                  "AI evaluates performance",
                  "pronunciation and timing assessment",
                ]}
              />
            </SoftCard>
          </div>

          <BigStatement
            title="The learner does not only memorize sentences."
            tone="purple"
            compact
          >
            The learner rehearses timing, response, pronunciation,
            and participation inside structured interaction.
          </BigStatement>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#f3e8ff,_transparent_30%),radial-gradient(circle_at_bottom_right,_#dbeafe,_transparent_30%),linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Support And Progression",
      density: "dense",
      content: (
        <div className="mx-auto max-w-6xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-blue-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">
              Guided difficulty
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              Support gradually disappears as the learner becomes more confident.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Hints function as temporary scaffolding.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            The system can provide different levels of support while keeping
            the learner inside the interaction loop.
          </p>

          <ComparisonTable
            leftTitle="Hint type"
            rightTitle="Learning function"
            leftColor="blue"
            rightColor="purple"
            rows={[
              ["first-word cue", "small retrieval support"],
              ["shuffled words", "sentence reconstruction"],
              ["audio replay", "prosody and pronunciation support"],
              ["spoken line + text", "maximum scaffold"],
            ]}
          />

          <BigStatement
            title="More support gives lower scores."
            tone="green"
            compact
          >
            The scoring system rewards increasing independence,
            while still allowing learners to continue practicing safely.
          </BigStatement>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_30%),linear-gradient(135deg,#ffffff,#eff6ff)]",
    },

    {
      title: "Why The Game Loop Supports Learning",
      density: "dense",
      content: (
        <div className="mx-auto max-w-6xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-emerald-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">
              Pedagogical logic
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              The game mechanics are derived from rehearsal, retrieval,
              scaffolding, and repeated exposure to useful language patterns.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            The interaction loop supports speech automaticity.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            The learner responds to a spoken cue, not just a written prompt.
            This trains pronunciation, intonation, rhythm, and timing inside
            a realistic dialogue exchange.
          </p>

          <ComparisonTable
            leftTitle="Learning principle"
            rightTitle="Interactive implementation"
            leftColor="green"
            rightColor="purple"
            rows={[
              ["repeated language exposure", "hear dialogue patterns many times"],
              ["chunk and pattern learning", "practice complete useful responses"],
              ["retrieval practice", "produce missing lines from memory"],
              ["prosodic response", "answer spoken cues with rhythm and intonation"],
              ["gradual scaffolding removal", "reduce hints over time"],
            ]}
          />

          <BigStatement
            title="The score measures independence, delivery, and timing."
            tone="green"
            compact
          >
            Fewer hints, fewer retries, clearer pronunciation, and more natural intonation
            show movement from guided rehearsal toward increasingly automatic spoken participation.
          </BigStatement>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dcfce7,_transparent_30%),radial-gradient(circle_at_bottom_right,_#f3e8ff,_transparent_30%),linear-gradient(135deg,#ffffff,#f8fafc)]",
    },

    {
      title: "Challenge & Shared Rehearsal",
      density: "dense",
      content: (
        <div className="mx-auto max-w-6xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-purple-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">
              Social practice layer
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              When a learner is ready, the same scene can become a challenge,
              a shared recording, or preparation for offline rehearsal.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Rehearsal becomes social when learners perform the same scene.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            Learners can challenge others on the same role, perform against
            another user’s recorded lines, and receive AI feedback with optional
            native-speaker review.
          </p>

          <ComparisonTable
            leftTitle="Social mechanic"
            rightTitle="Learning value"
            leftColor="purple"
            rightColor="green"
            rows={[
              ["challenge another learner", "same scene creates fair comparison"],
              ["use another user’s cue lines", "practice feels more alive"],
              ["AI scores pronunciation and timing", "feedback remains immediate"],
              ["native speaker can review", "human assessment can override AI"],
            ]}
          />

          <BigStatement
            title="The long-term goal is purposeful social rehearsal."
            tone="purple"
            compact
          >
            Online challenges can connect naturally to language cafés,
            reading groups, workshops, and offline dialogue practice.
          </BigStatement>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#f3e8ff,_transparent_30%),radial-gradient(circle_at_bottom_right,_#dcfce7,_transparent_30%),linear-gradient(135deg,#ffffff,#faf5ff)]",
    },
  ],
},

    business: {
      label: "Business",
      description: "monetization and team",
      slides: [
        {
          title: "Monetization & Partnerships",
          density: "medium",
          content: (
            <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
              <ConceptMark emoji="💼" label="Business" sub="community-first model" />
              <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
                Monetization follows the learning model.
              </h3>
              <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
                Catchphraser can become infrastructure for organized speech practice,
                guided rehearsal, narrated content, institutional speaking activities,
                and creator-led courses.
              </p>

              <div className="grid gap-4 md:grid-cols-4 xl:gap-5">
                <EmojiCard
                  emoji="🎧"
                  title="Publishers"
                  text="narrated content as interactive practice"
                  className="ring-1 ring-blue-100"
                />
                <EmojiCard
                  emoji="🎓"
                  title="Courses"
                  text="structured courses and revenue sharing"
                  className="ring-1 ring-amber-100"
                />
                <EmojiCard
                  emoji="🧑‍🏫"
                  title="Guided activities"
                  text="paid rooms, workshops, rehearsal groups"
                  className="ring-1 ring-purple-100"
                />
                <EmojiCard
                  emoji="🏛️"
                  title="Institutions"
                  text="university language cafés and cohorts"
                  className="ring-1 ring-emerald-100"
                />
              </div>

              <BigStatement title="The first offer: structured social practice and course infrastructure." tone="green" compact>
                Free peer exchange can bring users in. Guided rooms, institutional cohorts,
                publisher content, creator courses, and specialized workshops create monetization.
              </BigStatement>
            </div>
          ),
          bgColor:
            "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_32%),radial-gradient(circle_at_bottom_right,_#dcfce7,_transparent_30%),linear-gradient(135deg,#ffffff,#f8fafc)]",
        },

        {
          title: "Long-Term Advantage",
          density: "medium",
          content: (
            <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
              <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
                The platform becomes harder to copy as the ecosystem grows.
              </h3>
              <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
                Learners, institutions, publishers, and guided events create accumulated value:
                speech data, content, relationships, and community habits.
              </p>

              <ComparisonTable
                leftTitle="Accumulated knowledge"
                rightTitle="Content network"
                leftColor="green"
                rightColor="amber"
                rows={[
                  ["learner attempts create signal", "native-speaker audio creates quality"],
                  ["peer comments reveal patterns", "publishers get a new channel"],
                  ["L1-specific errors accumulate", "content becomes interactive practice"],
                  ["recommendations improve", "better material attracts learners"],
                ]}
              />

              <BigStatement title="A competitor can copy a feature, but not the accumulated ecosystem." tone="amber" compact>
                The long-term advantage is L1-specific pronunciation data,
                licensed practice material, institutional relationships,
                and a growing speech community.
              </BigStatement>
            </div>
          ),
          bgColor: "bg-[linear-gradient(135deg,#ffffff,#fffbeb)]",
        },

        {
          title: "Content & Course Platform",
          density: "dense",
          content: (
            <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4">
              <h3 className="mx-auto max-w-5xl text-3xl font-black leading-tight text-gray-950 md:text-5xl">
                Narrated content and courses become interactive speech practice.
              </h3>

              <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-600 sm:text-lg">
                Audiobooks, dialogues, graded readers, pronunciation courses, and
                teacher-made materials can become rehearsal, shadowing, feedback, and
                collaborative speaking experiences.
              </p>

              <div className="grid gap-5 md:grid-cols-2">
                <SoftCard className="bg-gradient-to-br from-amber-50 to-white text-left ring-1 ring-amber-200">
                  <div className="mb-4 text-center">
                    <div className="mb-1 text-xs font-black uppercase tracking-[0.18em] text-amber-700">
                      Creator value
                    </div>

                    <h4 className="text-xl font-black text-gray-950 xl:text-2xl">
                      New distribution layer
                    </h4>
                  </div>

                  <NumberedList
                    accent="amber"
                    items={[
                      "turn narrated material into interactive speech training",
                      "subscription or course revenue",
                      "developer and teacher publishing portal",
                      "platform handles rehearsal and delivery",
                    ]}
                  />
                </SoftCard>

                <SoftCard className="bg-gradient-to-br from-blue-50 to-white text-left ring-1 ring-blue-200">
                  <div className="mb-4 text-center">
                    <div className="mb-1 text-xs font-black uppercase tracking-[0.18em] text-blue-700">
                      Learner value
                    </div>

                    <h4 className="text-xl font-black text-gray-950 xl:text-2xl">
                      Structured speaking progression
                    </h4>
                  </div>

                  <NumberedList
                    accent="blue"
                    items={[
                      "high-quality speech models",
                      "practice with AI and human feedback",
                      "reading + listening + speaking integration",
                      "social rehearsal around shared material",
                    ]}
                  />
                </SoftCard>
              </div>

              <BigStatement title="This is not only content licensing." tone="amber" compact>
                The platform transforms narrated material and expert-made courses into
                reusable speech-practice infrastructure.
              </BigStatement>
            </div>
          ),
          bgColor: "bg-[linear-gradient(135deg,#fffdf5,#fffbeb)]",
        },

        {
          title: "The Catchphraser Team",
          density: "team",
          content: (
            <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
              <div className="text-center">
                <h2 className="mb-3 text-3xl font-black leading-tight text-gray-950 md:text-5xl">
                  The Catchphraser Team
                </h2>
                <p className="mx-auto max-w-3xl text-base leading-relaxed text-gray-600 sm:text-lg xl:text-xl">
                  Researchers and language professionals building pronunciation training and rehearsal infrastructure.
                </p>
              </div>

              <div className="mx-auto grid max-w-6xl gap-5 md:grid-cols-3 xl:gap-6">
                {[
                  [
                    "it.jpg",
                    "Dr. Inna Tolskaya",
                    "CEO – Product & Linguistics",
                    "itolsk@gmail.com",
                    "Linguist, project manager, and software developer with a passion for innovation and e-learning.",
                    "from-blue-50 to-indigo-50 border-blue-200 text-blue-600",
                    "IT",
                  ],
                  [
                    "oh.jpg",
                    "Assoc. Prof. Olaf Husby",
                    "CCO – Teaching & Didactics",
                    "olafhusby@gmail.com",
                    "Phonetician, second-language educator, course developer, and Norwegian dialects evangelist.",
                    "from-purple-50 to-violet-50 border-purple-200 text-purple-600",
                    "OH",
                  ],
                  [
                    "ds.jpg",
                    "Dr. Dmitry Shcherbin",
                    "CTO/CPO – Tech & Research",
                    "dmitry.shcherbin@gmail.com",
                    "Product leader, software developer, data analyst, and researcher with an interest in practical applications.",
                    "from-cyan-50 to-teal-50 border-cyan-200 text-cyan-600",
                    "DS",
                  ],
                ].map(([img, name, role, email, bio, cardColor, initials]) => (
                  <div
                    key={name}
                    className={`rounded-3xl border bg-gradient-to-br ${cardColor} p-4 shadow-[0_18px_60px_rgba(15,23,42,0.08)] xl:p-5`}
                  >
                    <div className="text-center">
                      <div className="relative mx-auto mb-4 flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border-4 border-white bg-gray-100 shadow-xl xl:h-24 xl:w-24">
                        <div className="absolute inset-0 flex items-center justify-center text-3xl font-black text-gray-500">
                          {initials}
                        </div>
                        <img
                          src={asset(img)}
                          alt={name}
                          className="relative z-10 h-full w-full object-cover"
                          onError={(event) => {
                            event.currentTarget.style.display = "none";
                          }}
                        />
                      </div>

                      <h3 className="mb-2 text-lg font-black text-gray-950 xl:text-xl">
                        {name}
                      </h3>
                      <p className="mb-2 text-sm font-bold">{role}</p>
                      <a href={`mailto:${email}`} className="break-all text-xs hover:underline">
                        {email}
                      </a>
                    </div>

                    <div className="mt-4 rounded-2xl border border-white/60 bg-white/85 p-4 shadow-sm backdrop-blur-sm">
                      <p className="text-sm leading-relaxed text-gray-700">{bio}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-center">
                <p className="mb-1 text-sm text-gray-700 sm:text-base">
                  Interested in collaboration, testing, or research partnerships?
                </p>
                <a href="mailto:info@catchphraser.com" className="text-base font-bold text-blue-600 hover:underline">
                  info@catchphraser.com
                </a>
              </div>
            </div>
          ),
          bgColor:
            "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_32%),radial-gradient(circle_at_bottom_right,_#ecfeff,_transparent_30%),linear-gradient(135deg,#ffffff,#f8fafc)]",
        },
      ],
    },
  };
};